import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { toast } from "react-toastify"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { TopBar } from "components/navigation/TopBar"

import { ModalCreateTag } from "features/tags/modals/ModalCreateTag"
import { AutomaticTagsTab } from "features/tags/tabs/AutomaticTagsTab"
import { ManualTagsTab } from "features/tags/tabs/ManualTagsTab"
import { RunsHistoryTab } from "features/tags/tabs/RunsHistoryTab"

import { useProps } from "contexts/PropsContext"

import { tagsService } from "services/tags/tags.service"
import { RuleFieldDef, TagRule, TagWithUsage } from "services/tags/type"

enum Tabs {
  ManualTags = "Manual tags",
  AutomaticTags = "Automatic tags",
  RunsHistory = "Runs history"
}

const TAB_PARAM = "tab"

const TAB_SLUGS: Record<Tabs, string> = {
  [Tabs.ManualTags]: "manual",
  [Tabs.AutomaticTags]: "automatic",
  [Tabs.RunsHistory]: "runs"
}

const TAB_BY_SLUG = Object.entries(TAB_SLUGS).reduce<Record<string, Tabs>>((acc, [tab, slug]) => {
  acc[slug] = tab as Tabs
  return acc
}, {})

export const TagManagementPage: FC = () => {
  const { darkMode } = useProps()
  const { t: tr } = useTranslation("translation", { useSuspense: false })

  const [searchParams, setSearchParams] = useSearchParams()
  const tab = TAB_BY_SLUG[searchParams.get(TAB_PARAM) ?? ""] ?? Tabs.ManualTags
  const tabs = useMemo(() => Object.values(Tabs), [])

  const setTab = useCallback(
    (next: Tabs) => {
      const params = new URLSearchParams(searchParams)
      params.set(TAB_PARAM, TAB_SLUGS[next])
      setSearchParams(params, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  const [loading, setLoading] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  // shared data
  const [automaticTags, setAutomaticTags] = useState<TagWithUsage[]>([])
  const [rules, setRules] = useState<TagRule[]>([])
  const [fields, setFields] = useState<RuleFieldDef[]>([])

  // modal state
  const [openModal, setOpenModal] = useState(false)
  const [editTag, setEditTag] = useState<TagWithUsage | null>(null)
  const [editRule, setEditRule] = useState<TagRule | null>(null)
  const [modalDefaultTab, setModalDefaultTab] = useState<"manual" | "auto">("manual")

  const loadFieldsOnce = useCallback(async () => {
    const fieldsRes = await tagsService.getFields()
    setFields(fieldsRes?.fields ?? [])
  }, [])

  const loadAutomaticTags = useCallback(async () => {
    const rows = await tagsService.getTagsStats({ isAutomatic: true })
    setAutomaticTags(rows ?? [])
  }, [])

  const loadRules = useCallback(async () => {
    const res = await tagsService.getRules({
      page: 1,
      pageSize: 200,
      sortRow: "priority",
      sortOrder: "desc"
    })
    setRules(res?.data ?? [])
  }, [])

  const onGlobalRefresh = useCallback(async () => {
    try {
      await Promise.all([loadFieldsOnce(), loadAutomaticTags(), loadRules()])
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to refresh")
    } finally {
      setRefreshKey((n) => n + 1)
    }
  }, [loadFieldsOnce, loadAutomaticTags, loadRules])

  useEffect(() => {
    setLoading(true)
    onGlobalRefresh().finally(() => setLoading(false))
  }, [onGlobalRefresh])

  // modal open helpers
  const openCreateManual = useCallback(() => {
    setEditTag(null)
    setEditRule(null)
    setModalDefaultTab("manual")
    setOpenModal(true)
  }, [])

  const openCreateAuto = useCallback(() => {
    setEditTag(null)
    setEditRule(null)
    setModalDefaultTab("auto")
    setOpenModal(true)
  }, [])

  const openEditManual = useCallback((t: TagWithUsage) => {
    setEditTag(t)
    setEditRule(null)
    setModalDefaultTab("manual")
    setOpenModal(true)
  }, [])

  const openEditAutoRule = useCallback((r: TagRule) => {
    setEditRule(r)
    setEditTag(null)
    setModalDefaultTab("auto")
    setOpenModal(true)
  }, [])

  const tagTabLabels: Record<Tabs, string> = {
    [Tabs.ManualTags]: tr("tags.tabs.manualTags"),
    [Tabs.AutomaticTags]: tr("tags.tabs.automaticTags"),
    [Tabs.RunsHistory]: tr("tags.tabs.runsHistory")
  }

  const tabBase = "flex cursor-pointer items-center text-left py-3 rounded-lg px-4 text-base border transition-all"

  return (
    <div className={clsx(darkMode && "dark")}>
      <div className="w-full max-w-[100vw] p-4 pt-0 sm:p-8 sm:pt-0">
        <TopBar title={tr("pages.tagManagement.title")} />

        <div className="tabs gap-2 py-3">
          {tabs.map((t) => {
            const isActive = tab === t

            return (
              <a
                key={t}
                onClick={() => setTab(t as Tabs)}
                className={clsx(
                  tabBase,
                  "text-[rgba(6,7,21,1)] dark:text-white",
                  isActive
                    ? "text-white bg-[rgba(139,92,246,1)] border-[rgba(139,92,246,1)]"
                    : "bg-white dark:bg-[#0C0C1A] border-[rgba(139,92,246,0.8)] dark:border-transparent hover:border-[rgba(139,92,246,1)] hover:bg-[rgba(139,92,246,0.2)] dark:hover:bg-[rgba(139,92,246,0.1)]"
                )}
              >
                {tagTabLabels[t] || t}
              </a>
            )
          })}
        </div>

        <div className="mt-4 rounded-xl border border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)] bg-white dark:bg-[#0C0C1A] p-4">
          {tab === Tabs.ManualTags && (
            <ManualTagsTab
              loading={loading}
              setLoading={setLoading}
              refreshKey={refreshKey}
              onCreate={openCreateManual}
              onEdit={openEditManual}
              onGlobalRefresh={onGlobalRefresh}
            />
          )}

          {tab === Tabs.AutomaticTags && (
            <AutomaticTagsTab
              loading={loading}
              setLoading={setLoading}
              refreshKey={refreshKey}
              automaticTags={automaticTags}
              onCreate={openCreateAuto}
              onEdit={openEditAutoRule}
              onGlobalRefresh={onGlobalRefresh}
              onRulesLoaded={setRules}
            />
          )}

          {tab === Tabs.RunsHistory && (
            <RunsHistoryTab
              loading={loading}
              setLoading={setLoading}
              refreshKey={refreshKey}
              rules={rules}
              onGlobalRefresh={onGlobalRefresh}
            />
          )}
        </div>

        <ModalCreateTag
          open={openModal}
          defaultTab={modalDefaultTab}
          onClose={() => setOpenModal(false)}
          onSaved={async () => {
            setOpenModal(false)
            setLoading(true)
            try {
              await onGlobalRefresh()
            } finally {
              setLoading(false)
            }
          }}
          editManualTag={editTag}
          editRule={editRule}
          availableAutomaticTags={automaticTags}
          fields={fields}
        />
      </div>
    </div>
  )
}
